/**
 * Exclusive salt grouping for Workflow Stage 3 (Nutrient Salt Management).
 *
 * Final category names (each material → one group only):
 *   Nitrogen
 *   Phosphorus
 *   Potassium
 *   Magnesium
 *   Calcium
 *   Trace Element
 *   Sulphate - Trace Elements
 *   Chelated Trace Elements
 *   Other
 *
 * Classification order is priority-based (first match wins).
 */

export type SaltCategory =
  | 'Nitrogen'
  | 'Phosphorus'
  | 'Potassium'
  | 'Magnesium'
  | 'Calcium'
  | 'Trace Element'
  | 'Sulphate - Trace Elements'
  | 'Chelated Trace Elements'
  | 'Other';

export const SALT_CATEGORY_ORDER: SaltCategory[] = [
  'Nitrogen',
  'Phosphorus',
  'Potassium',
  'Magnesium',
  'Calcium',
  'Trace Element',
  'Sulphate - Trace Elements',
  'Chelated Trace Elements',
  'Other',
];

export const SALT_CATEGORY_LABELS: Record<SaltCategory, string> = {
  Nitrogen: '1. Nitrogen',
  Phosphorus: '2. Phosphorus',
  Potassium: '3. Potassium',
  Magnesium: '4. Magnesium',
  Calcium: '5. Calcium',
  'Trace Element': '6. Trace Element',
  'Sulphate - Trace Elements': '7. Sulphate — Trace Elements',
  'Chelated Trace Elements': '8. Chelated Trace Elements',
  Other: '9. Other',
};

type SaltLike = {
  name: string;
  formula?: string | null;
  elements?: Array<{
    percentage?: number;
    element?: { symbol?: string };
    elementSymbol?: string;
  }>;
};

function norm(s: string): string {
  return (s || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // strip accents on NO₃ etc. if decomposed
    .replace(/·/g, '.')
    .trim();
}

function symbolsOf(salt: SaltLike): string[] {
  if (!salt.elements?.length) return [];
  return salt.elements
    .map((e) => {
      const raw = e.element?.symbol || e.elementSymbol || '';
      if (raw.startsWith('N_')) return 'N';
      return raw;
    })
    .filter(Boolean);
}

function hasSymbol(symbols: string[], ...want: string[]): boolean {
  return want.some((w) => symbols.includes(w));
}

/** Primary nutrient from composition (%): highest of Ca, Mg, K, P, N, S, micros */
function primaryFromElements(salt: SaltLike): string | null {
  if (!salt.elements?.length) return null;
  let best: { sym: string; pct: number } | null = null;
  for (const e of salt.elements) {
    let sym = e.element?.symbol || e.elementSymbol || '';
    if (sym.startsWith('N_')) sym = 'N';
    const pct = Number(e.percentage) || 0;
    if (!sym || pct <= 0) continue;
    // skip Na/Cl as "primary fertilizer identity" when other nutrients exist
    if (!best || pct > best.pct) best = { sym, pct };
  }
  return best?.sym ?? null;
}

/**
 * Classify a salt into exactly one exclusive group.
 * Priority matches product rules (chelate / Ca / Mg / K before generic N).
 */
export function classifySalt(salt: SaltLike): SaltCategory {
  const name = norm(salt.name);
  const formula = norm(salt.formula || '');
  const symbols = symbolsOf(salt);
  const primary = primaryFromElements(salt);

  // --- 9. Other: dev / NPK blends / junk ---
  if (
    /^test[_-]?all$/.test(name) ||
    /^si[_-]?test$/.test(name) ||
    name.includes('test_all') ||
    name.includes('si_test') ||
    name.includes('input formula')
  ) {
    return 'Other';
  }
  // Tailored / complete fertilizers e.g. 20-20-20, 13-0-46
  if (/\b\d{1,2}\s*-\s*\d{1,2}\s*-\s*\d{1,2}\b/.test(name)) {
    return 'Other';
  }
  if (/tailored|complete fertilizer|npk blend|compound fertilizer/.test(name)) {
    return 'Other';
  }

  // --- 8. Chelated Trace Elements ---
  if (/edta|dtpa|eddha|chelate|chelated/.test(name) || /edta|dtpa|eddha/.test(formula)) {
    return 'Chelated Trace Elements';
  }

  // --- 7. Sulphate — Trace Elements (Fe/Mn/Zn/Cu sulfates only) ---
  const isSulfate =
    /sulfate|sulphate|so4/.test(name) || /so4/.test(formula);
  const isTraceMetalSulfate =
    isSulfate &&
    (/copper|cupric|iron|ferrous|ferric|manganese|zinc|mn\b|zn\b|cu\b|fe\b/.test(name) ||
      hasSymbol(symbols, 'Cu', 'Fe', 'Mn', 'Zn'));
  // Exclude bulk Mg/Ca/K/NH4 sulfates from this group
  if (
    isTraceMetalSulfate &&
    !/magnesium|calcium|potassium|ammonium|epsom/.test(name)
  ) {
    return 'Sulphate - Trace Elements';
  }

  // --- 5. Calcium (before N — calcium nitrate is Calcium, not Nitrogen) ---
  if (
    /calcium|ca\(no3|caso4|caco3|ca\(h2po4/.test(name) ||
    /ca\(no3|caso4|caco3/.test(formula) ||
    primary === 'Ca'
  ) {
    // Don't steal chelates / pure micro mixes
    if (!/edta|dtpa|eddha|chelate/.test(name)) {
      return 'Calcium';
    }
  }

  // --- 4. Magnesium ---
  if (
    /^mg\b|magnesium|mg\(no3|mgso4|mgco3|epsom/.test(name) ||
    /mg\(no3|mgso4|mgco3/.test(formula) ||
    primary === 'Mg'
  ) {
    if (!/edta|dtpa|eddha|chelate/.test(name)) {
      return 'Magnesium';
    }
  }

  // --- 3. Potassium (includes potassium nitrate — not Nitrogen) ---
  if (
    /potassium|\bkno3\b|\bkcl\b|\bk2so4\b|\bkh2po4\b|\bk2hpo4\b|\bk2co3\b|mkp\b/.test(
      name
    ) ||
    /kno3|kcl|k2so4|kh2po4|k2hpo4|k2co3|c6h5k3o7/.test(formula) ||
    primary === 'K'
  ) {
    if (!/edta|dtpa|eddha|chelate/.test(name)) {
      return 'Potassium';
    }
  }

  // --- 2. Phosphorus — MAP/DAP style ammonium phosphates (+ phosphoric acid) ---
  if (
    /ammonium monobasic phosphate|ammonium dibasic phosphate|monoammonium phosphate|diammonium phosphate|\bmap\b|\bdap\b/.test(
      name
    ) ||
    ( /ammonium/.test(name) && /phosphate/.test(name) ) ||
    /phosphoric acid|h3po4/.test(name) ||
    formula === 'h3po4' ||
    formula.includes('nh4h2po4') ||
    formula.includes('(nh4)2hpo4')
  ) {
    return 'Phosphorus';
  }
  // Calcium monobasic phosphate already Calcium; remaining phosphates without K/Ca/Mg
  if (
    /phosphate|phosphoric/.test(name) &&
    !/potassium|calcium|magnesium|ammonium/.test(name) &&
    primary === 'P'
  ) {
    return 'Phosphorus';
  }

  // --- 6. Trace Element (non-chelate, non-TE-sulfate) ---
  if (
    /boric|borax|borate|molybdate|\bmicro\b|ch\s*-?\s*micro|ch micro/.test(name) ||
    /h3bo3|na2b4o7|na2moo4/.test(formula) ||
    // copper/zinc nitrate hexahydrate as TE sources
    /copper nitrate|zinc nitrate|cupric nitrate/.test(name) ||
    hasSymbol(symbols, 'B', 'Mo') && !hasSymbol(symbols, 'Ca', 'Mg', 'K', 'N') ||
    (primary && ['Fe', 'Mn', 'Zn', 'Cu', 'B', 'Mo', 'Si'].includes(primary) && !isSulfate)
  ) {
    // Avoid reclassifying bulk N/P/K/Ca/Mg already handled
    if (!/ammonium|potassium|calcium|magnesium|sodium nitrate/.test(name)) {
      return 'Trace Element';
    }
    // CH-micro / multi-micro mixes
    if (/\bmicro\b|ch\s*-?\s*micro/.test(name)) {
      return 'Trace Element';
    }
  }
  // Multi-micro commercial (CH - micro)
  if (/\bmicro\b/.test(name) || name.includes('ch - micro') || name === 'ch - micro') {
    return 'Trace Element';
  }
  // copper/zinc nitrate still TE even if N present
  if (/copper nitrate|zinc nitrate/.test(name)) {
    return 'Trace Element';
  }
  // boric / molybdate / borax
  if (/boric|borax|borate|molybdate/.test(name)) {
    return 'Trace Element';
  }

  // --- 1. Nitrogen (remaining N sources only) ---
  // Explicit exclusive list patterns: NH4Cl, NH4NO3, (NH4)2SO4, NaNO3
  if (
    /ammonium chloride|ammonium nitrate|ammonium sulfate|ammonium sulphate|sodium nitrate/.test(
      name
    ) ||
    /nh4cl|nh4no3|\(nh4\)2so4|nano3/.test(formula)
  ) {
    return 'Nitrogen';
  }
  // Other ammonium/nitrate that weren't Ca/Mg/K/Cu/Zn
  if (
    (/ammonium|nitrate|nh4|no3/.test(name) || /nh4|no3/.test(formula) || primary === 'N') &&
    !/potassium|calcium|magnesium|copper|zinc|iron|manganese/.test(name)
  ) {
    return 'Nitrogen';
  }

  // --- 9. Other ---
  return 'Other';
}

export function sortSaltsByName<T extends { name: string }>(salts: T[]): T[] {
  return [...salts].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
}

export function groupSaltsByCategory<T extends SaltLike>(
  salts: T[]
): { category: SaltCategory; label: string; salts: T[] }[] {
  const buckets = new Map<SaltCategory, T[]>();
  for (const cat of SALT_CATEGORY_ORDER) {
    buckets.set(cat, []);
  }
  for (const salt of salts) {
    const cat = classifySalt(salt);
    buckets.get(cat)!.push(salt);
  }
  return SALT_CATEGORY_ORDER.map((category) => ({
    category,
    label: SALT_CATEGORY_LABELS[category],
    salts: sortSaltsByName(buckets.get(category) || []),
  })).filter((g) => g.salts.length > 0);
}
